"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { ChangePasswordFormSchema } from "./schema";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import hooks from "@/hooks";
import { useToast } from "@/hooks/use-toast";
import helpers from "@/helpers";

type ChangePassword = z.infer<typeof ChangePasswordFormSchema>;

interface IChangePasswordForm {
  id: string & {};
}

const ChangePasswordForm = ({ id }: IChangePasswordForm) => {
  const t = useTranslations("Profile.password.form");

  const { toast } = useToast();

  const form = useForm<ChangePassword>({
    resolver: zodResolver(ChangePasswordFormSchema),
    defaultValues: {
      lastPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const changePasswordService = hooks.useChangePassword({
    onSuccess: async () => {
      toast({
        title: t("toasts.success.changePassword.title"),
        description: t("toasts.success.changePassword.description"),
        variant: "success",
      });
      setTimeout(async () => {
        await helpers.logoutHelper();
      }, 2000);
    },
    onError: (error: unknown) => {
      toast({
        title: t("toasts.unexpectedError.changePassword.title"),
        description:
          (error as Error).message ??
          t("toasts.unexpectedError.changePassword.description"),
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: ChangePassword) {
    await changePasswordService.mutateAsync({
      id,
      lastPassword: data.lastPassword,
      newPassword: data.newPassword,
    });
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="p-8 w-full flex flex-col items-center gap-y-6"
      >
        <FormFields.InputFormField
          form={form}
          formName="lastPassword"
          label={t("label.currentPassword")}
          placeholder={t("placeholder.currentPassword")}
          type="password"
        />
        <FormFields.InputFormField
          form={form}
          formName="newPassword"
          label={t("label.newPassword")}
          placeholder={t("placeholder.newPassword")}
          type="password"
        />
        <FormFields.InputFormField
          form={form}
          formName="confirmNewPassword"
          label={t("label.confirmNewPassword")}
          placeholder={t("placeholder.confirmNewPassword")}
          type="password"
        />
        <Buttons.DefaultButton
          className="py-6"
          type="submit"
          title={t("button")}
          isLoading={changePasswordService.isPending}
        />
      </form>
    </Form>
  );
};

export default ChangePasswordForm;
